import { Component, OnInit, ViewChild, inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Aufgabe } from 'src/app/domain/model/aufgabe';
import { Dokument } from 'src/app/domain/model/dokument';
import { environment } from 'src/environments/environment';
import { AufgabeService } from '../../service/aufgabe/aufgabe.service';
import { ZuweisungenDataSource } from './zuweisungen-data-source';
import { AuthenticationService } from '../../service/auth/authentication.service';
import { UflaUser } from '../../service/auth/ufla-user';
import { ConfirmDialogService } from 'src/app/service/common/confirm-dialog/confirm-dialog.service';
import { HttpClient } from '@angular/common/http';
import { Zuweisung } from '../../domain/model/zuweisung';
import { EmailTemplateDialogService } from '../../service/common/email-template-dialog/email-template-dialog.service';
import { MatSort } from '@angular/material/sort';
import { UpAndDownloadComponent } from '../../common/upload/up-and-download-component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'ufla-aufgabe-details',
    templateUrl: './aufgabe-details.component.html',
    styleUrls: [
        './aufgabe-details.component.scss',
    ],
    standalone: false,
})
export class AufgabeDetailsComponent extends UpAndDownloadComponent implements OnInit {
    private aufgabeService = inject(AufgabeService);
    authenticationService = inject(AuthenticationService);
    protected http: HttpClient;
    private activatedRoute = inject(ActivatedRoute);
    private confirmDialogService = inject(ConfirmDialogService);
    private emailTemplateService = inject(EmailTemplateDialogService);
    private router = inject(Router);

    public uflaUser: UflaUser;

    public aufgabe$: Observable<Aufgabe>;
    public dokumente$: Observable<Dokument[]>;

    public zuweisungenDataSource = new ZuweisungenDataSource();
    public columnsToDisplay = [
        'ersteller.name',
        'ersteller.type',
        'ersteller.kundenBetreuer',
        'gelesen',
        'replyFileName',
    ];
    public columnsForDocuments = [
        'fileName',
        'lastModified',
        'icon',
    ];
    public allFilesDocument: Dokument = new Dokument();

    matSort: MatSort;

    anzahlZuweisungen = 0;
    anzahlZuweisungenBestaetigt = 0;
    aufgabenId: string;
    public emailSubject = 'TNB-Portal UFLA: Rückfrage zu einem nicht abgeschlossenem Vorgang';
    public emailBody = 'Kontaktaufnahme zu Vorgang {0} vom Typ {1} aus dem Jahr {2} \n\nSehr geehrte Damen und Herren, \n';
    public bccAddresses = '';
    public bccAddressesForMailto = '';
    public westnetzAdmin: boolean;

    constructor() {
        super();

        this.authenticationService.user$
            .pipe(takeUntilDestroyed())
            .subscribe((user) => {
                this.uflaUser = user;
                this.westnetzAdmin =
                    this.authenticationService.isAdmin(this.uflaUser) &&
                    !this.authenticationService.isUenb(this.uflaUser);
            });
    }

  @ViewChild(MatSort, { static: false })
    set sort(sort: MatSort) {
        this.matSort = sort;

        if (this.matSort) {
            this.zuweisungenDataSource.sort = this.matSort;
        }
    }

  ngOnInit() {
      this.activatedRoute.paramMap.subscribe((paramMap) => {
          this.aufgabenId = paramMap.get('id');
          this.dokumente$ = this.aufgabeService.listCreatorDocumentsAsCreator(this.aufgabenId);

          // load aufgabe and replies
          const aufgabeRaw$ = this.aufgabeService.getAufgabe(this.aufgabenId);
          const replies$ = this.aufgabeService.listAllReplies(this.aufgabenId);

          // set reply filename to zuweisung inside aufgabe if reply is present and zuweisung is done
          this.aufgabe$ = forkJoin(aufgabeRaw$, replies$)
              .pipe(
                  map(([
                      aufgabe,
                      replies,
                  ]) => {
                      this.setEmailData(aufgabe);
                      aufgabe.zuweisungen.forEach((zuweisung) => {
                          if (zuweisung.gelesen) {
                              replies.forEach((reply) => {
                                  if (Number(reply.zuweisungId) === zuweisung.id) {
                                      zuweisung.replyFileName = reply.fileName;
                                  }
                              });
                          }
                      });
                      return aufgabe;
                  }));
      });

      this.aufgabe$.subscribe(aufgabe => {
          this.anzahlZuweisungen = aufgabe.zuweisungen.length;
          this.anzahlZuweisungenBestaetigt = aufgabe.zuweisungen.filter(z => z.gelesen).length;
          this.zuweisungenDataSource.data = aufgabe.zuweisungen;
      });
      this.zuweisungenDataSource.sortingDataAccessor = (item, property) => {
          switch (property) {
              case 'ersteller.name':
                  return item.zugewiesen.name;
              case 'ersteller.type':
                  return item.zugewiesen.typ;
              case 'ersteller.kundenBetreuer':
                  return item.zugewiesen.kundenBetreuer;
              case 'ersteller.stromNetzbetreiberNummer':
                  return item.zugewiesen.stromNetzbetreiberNummer;
              case 'gelesen':
                  return item.gelesen;
              default:
                  return item[property];
          }
      };
  }

  public downloadDocument(aufgabeId: string, dokument: Dokument) {
      const fileDownloadUrl = environment.API_URL + '/attachment/aufgabe/' + aufgabeId
      + '/dokument/' + encodeURIComponent(dokument.fileName);
      this.createAndClickDownloadLink(dokument, fileDownloadUrl);
  }

  public downloadReply(zuweisung: Zuweisung) {
      const fileDownloadUrl = environment.API_URL + '/attachment/zuweisung/' + zuweisung.id + '/dokument/' +
      encodeURIComponent(zuweisung.replyFileName);

      this.createAndClickDownloadLink(zuweisung, fileDownloadUrl);
  }

  public downloadAllReplies() {
      const fileDownloadUrl = environment.API_URL + '/attachment/aufgabe/' + this.aufgabenId + '/dokument/all';
      this.createAndClickDownloadLink(this.allFilesDocument, fileDownloadUrl);
  }

  public canDelete(aufgabe: Aufgabe): boolean {
      return (Number(aufgabe.ersteller.id) === Number(this.uflaUser.organisationsId)) &&
      this.authenticationService.isAdmin(this.uflaUser);
  }

  public async delete(aufgabe: Aufgabe) {
      if (await this.confirmDialogService.showDialog(
          'Sind Sie sicher, dass Sie diesen Vorgang löschen möchten? Vorgänge, die gelöscht wurden, können '
      + 'nicht wiederhergestellt werden.', 'Hinweis zum Löschen', 'primary',
          'Ja', 'Nein')) {
          await this.aufgabeService.deleteAufgabe(String(aufgabe.id));
          await this.router.navigate([
              'dashboard',
          ]);
      }
  }

  public async showEmailTemplate() {
      if (await this.emailTemplateService.showEmailTemplate(
          this.bccAddresses, 'primary')) {
      }
  }

  private setEmailData(aufgabe: Aufgabe) {
      this.emailBody = this.emailBody
          .replace('{0}', aufgabe.name)
          .replace('{1}', aufgabe.type);

      if (aufgabe.frist) {
          const fristDate = new Date(aufgabe.frist);
          this.emailBody = this.emailBody.replace('{2}', fristDate.getFullYear().toString());
      }

      const offeneZuweisungen = aufgabe.zuweisungen.filter(z => !z.gelesen && z.zugewiesen.emailAdressen);

      if (offeneZuweisungen.length > 0) {
          this.bccAddresses = offeneZuweisungen.map(z => z.zugewiesen.emailAdressen
              .replace(/,/g, ';')).join(';');
          console.log('this.bccAddresses.length:' + this.bccAddresses.length);
          // when chrome urlencodes the bccaddresses, max ~300 encoded chars works -
          // otherwise the button does nothing onclick
          if (this.bccAddresses.length > 200) {
              this.bccAddressesForMailto = '';
          } else {
              this.bccAddressesForMailto = this.bccAddresses;
          }
      }
  }
}
